<template>
  <div id="mapcont">
    <div id="map"></div>
    <router-view :key="$route.path"></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css'; // <-- Import Leaflet CSS
import {kml} from 'togeojson'

// --- Leaflet Icon Definitions ---
// Note: Leaflet anchors are [x, y] from top-left corner.
// Size is [width, height].

const checkpointIcon = L.icon({
  iconUrl: 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-timepoint.png',
  iconSize: [18, 22],
  iconAnchor: [9, 22], // Center bottom
});

const finishIcon = L.icon({
  iconUrl: 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-finish.png',
  iconSize: [22, 22],
  iconAnchor: [0, 22], // Left bottom (as per original anchor) - adjust if needed
});

// Define functions to get athlete icons dynamically based on state
const getAthleteIcon = (athlete, isSelected, isFavourite) => {
  const gender = athlete.gender === 'male' ? 'male' : 'female'; // Default to female if not male
  let baseUrl;
  let size;
  let anchor;

  if (isSelected) {
    baseUrl = gender === 'male'
      ? 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-male-selected.png'
      : 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-female-selected.png';
    size = [50, 50];
    anchor = [25, 25]; // Center
  } else if (isFavourite) {
    baseUrl = gender === 'male'
      ? 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-favourite-male.png'
      : 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-favourite-female.png';
    size = [20, 20];
    anchor = [10, 10]; // Center
  } else {
    baseUrl = gender === 'male'
      ? 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-male.png'
      : 'https://d3nv7i3620sxr0.cloudfront.net/img/icon-map-female.png';
    size = [20, 20];
    anchor = [10, 10]; // Center
  }

  return L.icon({
    iconUrl: baseUrl,
    iconSize: size,
    iconAnchor: anchor,
  });
};


// Fix default icon path issue with webpack/build tools
// delete L.Icon.Default.prototype._getIconUrl;
// L.Icon.Default.mergeOptions({
//   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
//   iconUrl: require('leaflet/dist/images/marker-icon.png'),
//   shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
// });
// --- OR --- Provide explicit default icons if needed elsewhere


export default {
  // No need for the 'test' component import unless it's also being converted/used
  // components: {
  //   test: test
  // },
  data() {
    return {
      map: null, // Leaflet map instance
      routeLayer: null, // Leaflet layer for the main KML route
      carRouteLayer: null, // Separate layer for the car route if needed
      athletePathLayer: null, // Leaflet layer for the single athlete's polyline
      cpmarkers: [], // Array for Leaflet checkpoint markers
      // cplisteners: [], // No longer needed; manage with .on/.off
      athletemarkers: {}, // Object for Leaflet athlete markers { id: markerInstance }
      selected: '',
      // icons: {}, // Icons are now created dynamically or defined above
      // firebaseRefs: [], // Keep this if Firebase logic is still used
      showFavs: false,
      baseLayers: {}, // For layer control
      overlayLayers: {}, // For layer control
      layerControl: null, // To manage base/overlay layers
    };
  },
  computed: {
    ...mapGetters({
      mobile: 'mobile',
      searchTerm: 'search',
      checkpoints: 'checkpoints',
      favourites: 'favourites', // Ensure favourites is an array of IDs
      athletes: 'athletes',
      athlete: 'athlete',
      // polyline: 'routePolyline', // No longer used directly? Route comes from KML URL
      raceId: 'raceId',
      race: 'race',
      selectedCat: 'selectedCat',
    }),
    isMap() {
      // Check if the Leaflet map instance exists
      return this.map !== null;
    },
    filteredAthletes() {
      // Pre-filter athletes based on search, favourites, category for easier marker management
      if (!this.athletes) return {};

      const searchRegex = new RegExp(this.searchTerm, 'i');
      let athletesToShow = {};

      for (const id in this.athletes) {
        const ath = this.athletes[id];

        // Basic filtering logic
        // --- Refined Coordinate Check ---
        const lat = ath.properties.latitude;
        const lng = ath.properties.longitude;
        // Ensure lat/lng are actual, non-null numbers
        const hasValidCoords = typeof lat === 'number' && !isNaN(lat) && typeof lng === 'number' && !isNaN(lng);

        const isVisibleBySearch = !this.searchTerm || searchRegex.test(ath.first_name + ' ' + ath.last_name) || (ath.team && searchRegex.test(ath.team.name));
        const isVisibleByFav = !this.showFavs || this.favourites.includes(ath.id);
        const isVisibleByCat = this.selectedCat === 'All Categories' || ath.category === this.selectedCat;

        // Exclude finished/withdrawn unless specifically needed later
        const isTracking = !ath.isFinished && ath.withdrawn_at === null;

        if (isTracking && isVisibleBySearch && isVisibleByFav && isVisibleByCat && hasValidCoords) {
          if (typeof (ath.properties.latitude) !== 'undefined' && typeof (ath.properties.longitude) !== 'undefined'){
            athletesToShow[id] = ath;
          }
        }
      }
      return athletesToShow;
    }
  },
  watch: {
    raceId() {
      if (this.map) {
        this.clearRoute();
        this.clearAthleteMarkers(); // Clear existing markers
        this.setMapRoute(); // Load new route
        this.setRaceCheckpoints(); // Load new checkpoints
        this.updateAthleteMarkers(); // Add markers for the new race
      }
    },
    // Watch the computed property for efficient updates
    filteredAthletes(newAthletes, oldAthletes) {
      if (this.map) {
        this.updateAthleteMarkers();
      }
    },
    // Watch individual filters if needed for specific logic beyond filteredAthletes
    // searchTerm() { this.updateAthleteMarkers(); },
    // favourites() { this.updateAthleteMarkers(); },
    // showFavs() { this.updateAthleteMarkers(); },
    // selectedCat() { this.updateAthleteMarkers(); },

    checkpoints() {
      if (this.map) {
        this.setRaceCheckpoints();
      }
    },
    athlete(currentAthlete) {
      // Update or draw the single athlete's path
      if (this.map) {
        this.clearAthletePath(); // Remove previous path
        if (currentAthlete.fixes.length > 0) {
          const latLngs = currentAthlete.fixes.map((fix) => [fix.latitude, fix.longitude]);
          this.athletePathLayer = L.polyline(latLngs, {
            color: '#185371',
            weight: 1.5,
            opacity: 1.0,
          }).addTo(this.map);
        }
      }
      // Also update the icon style of the selected athlete's marker
      this.updateAthleteMarkers();
    },
    // polyline: function() { // This watcher seems redundant if route comes from KML
    //   if (this.map !== null) {
    //     this.clearRoute();
    //   }
    //   this.setMapRoute();
    // },
    '$route'(to) {
      // Update selected state based on route
      if (to.name === 'athlete' || to.name === 'mapathlete') {
        this.selected = to.params.id; // Make sure ID is correct type (string/number)
      } else {
        this.selected = ''; // Or based on 'checkpoint' route name if needed
      }
      // Update marker styles if selection changes
      if (this.map) {
        this.updateAthleteMarkers();
      }
    },
  },
  methods: {
    createMap() {
      if (this.map) { // Prevent re-initialization
        this.map.remove();
        this.map = null;
      }

      // Default center and zoom (adjust as needed)
      const initialCenter = [-29.62319, 28.2334698];
      const initialZoom = 7;

      this.map = L.map('map', {
        center: initialCenter,
        zoom: initialZoom,
        zoomControl: false, // Add manually later for positioning
        // Other Leaflet options if needed
      });

      // --- Tile Layers ---
      const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      });

      const openTopo = L.tileLayer('https://htonl.dev.openstreetmap.org/ngi-tiles/tiles/50k/{z}/{x}/{-y}.png', {
        attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        maxZoom: 17,
      });

      // Satellite Layer (Example using Esri)
      const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 18, // Check max zoom for the provider
      });


      this.baseLayers = {
        'OpenStreetMap': osm,
        'OpenTopoMap': openTopo,
        'Satellite': satellite,
      };

      // Add default layer
      osm.addTo(this.map);

      // --- Controls ---
      const zoomControlPosition = this.mobile ? 'bottomright' : 'topright';
      L.control.zoom({ position: zoomControlPosition }).addTo(this.map);
      L.control.scale({ position: 'bottomleft' }).addTo(this.map);

      // Layer Control (allows switching base layers)
      this.layerControl = L.control.layers(this.baseLayers, this.overlayLayers, {
        position: 'topright', // Or another position
        collapsed: true // Keep it compact
      }).addTo(this.map);

      // Custom Favourites Toggle Control
      this.createFilterControl();

      // --- Initial Data Loading ---
      this.setMapRoute();
      this.setRaceCheckpoints();
      this.updateAthleteMarkers(); // Initial population
    },

    async setMapRoute() {
      this.clearRoute(); // Clear existing route layers first

      if (!this.raceId || this.race.type === "no_route") {
        console.log("No route to display for this race type or ID.");
        return;
      }

      // --- Fetch and display the main race route ---
      try {
        // *** ADJUST THIS URL to your actual points endpoint ***
        const routeUrl = `https://app.sportraxs.com/api/tracking/races/${this.raceId}/route`;

        const response = await fetch(routeUrl);
        if (!response.ok) {
          throw new Error(`HTTP error loading route points! status: ${response.status}`);
        }
        const routePoints = await response.json(); // Parse the JSON response

        // Validate data - ensure it's an array and has points
        if (!Array.isArray(routePoints) || routePoints.length === 0) {
          console.warn(`No valid route points received for race ${this.raceId}`);
          return; // Nothing to draw
        }

        // Extract latitude and longitude into Leaflet's expected format [lat, lng]
        const latLngs = routePoints.map(point => {
          // Add checks if lat/lon might be missing or invalid in your API response
          if (typeof point.latitude === 'number' && typeof point.longitude === 'number') {
            return [point.latitude, point.longitude];
          }
          console.warn("Invalid point data encountered:", point);
          return null; // Mark invalid points
        }).filter(coord => coord !== null); // Filter out any invalid points

        if (latLngs.length === 0) {
          console.warn(`No valid coordinates found in route points for race ${this.raceId}`);
          return;
        }

        // --- Styling ---
        // Option A: Simple - Use a fixed color or the color from the first point
        const routeStyle = {
          color: '#' + (routePoints[0].colour || 'FF7800'), // Use first point's color (prepend #) or fallback
          weight: 3
        };

        // Option B: Multi-Colored Line (More Complex - see notes below)
        // This would involve creating multiple polylines grouped in a LayerGroup

        // --- Create Polyline (using simple style Option A) ---
        this.routeLayer = L.polyline(latLngs, routeStyle);

        // --- Add to map and layer control ---
        this.routeLayer.addTo(this.map);
        if (this.layerControl) {
          // Use a suitable name for the layer control
          this.overlayLayers['Race Route'] = this.routeLayer;
          this.layerControl.addOverlay(this.routeLayer, 'Race Route');
        }

        // --- Fit Bounds ---
        // Fit the map view to the newly added route
        if (this.routeLayer.getBounds().isValid()) {
          this.map.fitBounds(this.routeLayer.getBounds(), { padding: [50, 50] });
        }

      } catch (error) {
        console.error(`Error fetching or processing route points for race ${this.raceId}:`, error);
        // Optionally: display a user-friendly error message
      }

      // --- Handle specific logic for race 802 needing car route 804 ---
      if (this.race.id === 802) {
        try {
          // *** ADJUST THIS URL to your actual points endpoint for the car route ***
          const carRouteUrl = `/api/tracking/races/804/route_points?t=${Date.now()}`; // Example URL

          const carResponse = await fetch(carRouteUrl);
          if (!carResponse.ok) {
            throw new Error(`HTTP error loading car route points! status: ${carResponse.status}`);
          }
          const carRoutePoints = await carResponse.json();

          if (Array.isArray(carRoutePoints) && carRoutePoints.length > 0) {
            const carLatLngs = carRoutePoints.map(point => {
              if (typeof point.latitude === 'number' && typeof point.longitude === 'number') {
                return [point.latitude, point.longitude];
              }
              return null;
            }).filter(coord => coord !== null);

            if(carLatLngs.length > 0) {
              const carRouteStyle = {
                color: '#' + (carRoutePoints[0].colour || '0000FF'), // Different default color
                weight: 2,
                dashArray: '5, 5' // Dashed line for car route
              };
              this.carRouteLayer = L.polyline(carLatLngs, carRouteStyle); // Store separately

              this.carRouteLayer.addTo(this.map);
              if (this.layerControl) {
                this.overlayLayers['Car Route'] = this.carRouteLayer;
                this.layerControl.addOverlay(this.carRouteLayer, 'Car Route');
              }
              // Optional: Adjust map bounds again to include car route if desired
              // const combinedBounds = this.routeLayer.getBounds().extend(this.carRouteLayer.getBounds());
              // if (combinedBounds.isValid()) {
              //    this.map.fitBounds(combinedBounds, { padding: [50, 50] });
              // }
            }
          }
        } catch (error) {
          console.error(`Error fetching or processing car route points for race 804:`, error);
        }
      }
    }, // End of setMapRoute

    // Ensure clearRoute handles both potential layers
    clearRoute() {
      if (this.routeLayer) {
        this.map.removeLayer(this.routeLayer);
        if (this.layerControl && this.overlayLayers['Race Route']) {
          this.layerControl.removeLayer(this.routeLayer);
          delete this.overlayLayers['Race Route'];
        }
        this.routeLayer = null;
      }
      if (this.carRouteLayer) { // Also clear car route layer
        this.map.removeLayer(this.carRouteLayer);
        if (this.layerControl && this.overlayLayers['Car Route']) {
          this.layerControl.removeLayer(this.carRouteLayer);
          delete this.overlayLayers['Car Route'];
        }
        this.carRouteLayer = null;
      }
    },

    // --- Checkpoint Markers ---
    clearCheckpointMarkers() {
      this.cpmarkers.forEach(marker => marker.remove());
      this.cpmarkers = [];
      // Also remove from layer control if it was added as overlay
      if (this.layerControl && this.overlayLayers['Checkpoints']) {
        // If checkpoints were grouped, remove the group
        // Assuming individual markers weren't added to control here
        const cpGroup = this.overlayLayers['Checkpoints'];
        if(cpGroup) {
          this.layerControl.removeLayer(cpGroup);
          delete this.overlayLayers['Checkpoints'];
        }
      }
    },

    setRaceCheckpoints() {
      this.clearCheckpointMarkers();
      if (!this.checkpoints || this.checkpoints.length === 0) return;

      const bounds = L.latLngBounds([]);
      const checkpointGroup = L.layerGroup(); // Group checkpoints for easier management

      for (let i in this.checkpoints) {
        let cp = this.checkpoints[i]
        if (cp.point.latitude && cp.point.longitude) {
          const latLng = [parseFloat(cp.point.latitude), parseFloat(cp.point.longitude)];
          const icon = cp.isFinish ? finishIcon : checkpointIcon;

          const marker = L.marker(latLng, {
            icon: icon,
            // riseOnHover: true // Optional: makes marker prominent on hover
          }).addTo(checkpointGroup); // Add to the group

          // Add click listener
          marker.on('click', () => {
            this.handleCheckpointClick(cp.id);
          });

          // Optional: Add tooltip for name/info
          marker.bindTooltip(cp.name || `CP ${cp.id}`, { // Assuming CP has a name property
            direction: 'top',
            offset: L.point(0, -icon.options.iconSize[1] / 2) // Adjust offset
          });

          this.cpmarkers.push(marker); // Keep individual refs if needed elsewhere
          bounds.extend(latLng);
        }
      }


      checkpointGroup.addTo(this.map); // Add the whole group to the map
      // Add group to layer control as an overlay
      if (this.layerControl) {
        this.overlayLayers['Checkpoints'] = checkpointGroup;
        this.layerControl.addOverlay(checkpointGroup, 'Checkpoints');
      }

      // Fit map bounds to checkpoints only if no KML route was loaded (or prioritize KML bounds)
      // A better approach might be to collect all bounds (route, checkpoints) and fit once.
      if (bounds.isValid() && !this.routeLayer) {
        this.map.fitBounds(bounds, { padding: [50, 50] }); // Add padding
      } else if (bounds.isValid() && this.routeLayer) {
        // Optionally extend the route layer bounds with checkpoint bounds before fitting
        const combinedBounds = this.routeLayer.getBounds().extend(bounds);
        if (combinedBounds.isValid()) {
          this.map.fitBounds(combinedBounds, { padding: [50, 50] });
        }
      }
    },

    handleCheckpointClick(cpId) {
      // Use Vue Router to navigate
      console.log('Checkpoint clicked:', cpId);
      // self.$ma.trackEvent(...) // Keep tracking if needed
      const routeName = this.mobile ? 'mcheckpoint' : 'checkpoint';
      this.$router.push({ name: routeName, params: { id: cpId } });
      this.$store.dispatch('focusCP', cpId); // Keep Vuex action
    },


    // --- Athlete Markers ---
    clearAthleteMarkers() {
      Object.values(this.athletemarkers).forEach(marker => marker.remove());
      this.athletemarkers = {};
      // Also remove from layer control if it was added as overlay
      if (this.layerControl && this.overlayLayers['Athletes']) {
        const athGroup = this.overlayLayers['Athletes'];
        if(athGroup) {
          this.layerControl.removeLayer(athGroup);
          delete this.overlayLayers['Athletes'];
        }
      }
      // Clear Firebase listeners if they were tied to markers (original code was commented out)
      // this.firebaseRefs.forEach(refData => refData.ref.off('value', refData.onValueChange));
      // this.firebaseRefs = [];
    },

    // Use the computed filteredAthletes for updates
    updateAthleteMarkers() {
      if (!this.map) return;

      const athletesToDisplay = this.filteredAthletes;
      const displayedMarkerIds = Object.keys(this.athletemarkers);
      const athletesToDisplayIds = Object.keys(athletesToDisplay);

      let athleteGroup = this.overlayLayers['Athletes'];
      // Create group if it doesn't exist
      if (!athleteGroup) {
        athleteGroup = L.layerGroup().addTo(this.map);
        this.overlayLayers['Athletes'] = athleteGroup;
        if (this.layerControl) {
          this.layerControl.addOverlay(athleteGroup, 'Athletes');
        }
      }


      // 1. Remove markers that are no longer in the filtered list
      displayedMarkerIds.forEach(id => {
        if (!athletesToDisplay[id]) {
          this.athletemarkers[id].remove(); // Remove from map (implicitly from group)
          delete this.athletemarkers[id];
        }
      });

      // 2. Add or update markers for athletes in the filtered list
      athletesToDisplayIds.forEach(id => {
        const ath = athletesToDisplay[id];
        const latLng = [ath.properties.latitude, ath.properties.longitude];
        const isSelected = String(ath.id) === String(this.selected); // Ensure type consistency
        const isFavourite = this.favourites.includes(ath.id);
        const icon = getAthleteIcon(ath, isSelected, isFavourite);
        const name = ath.team_id === null ? `${ath.first_name} ${ath.last_name}` : ath.team.name;

        if (this.athletemarkers[id]) {
          // Update existing marker
          this.athletemarkers[id].setLatLng(latLng);
          this.athletemarkers[id].setIcon(icon);
          // Update tooltip content if necessary (e.g., if name changes)
          this.athletemarkers[id].setTooltipContent(name);

        } else {
          // Create new marker
          const marker = L.marker(latLng, {
            icon: icon,
            // Keep track of athlete data within the marker for filtering/identification
            athId: ath.id,
            athName: `${ath.first_name} ${ath.last_name}`, // For potential search within markers if needed later
            athCat: ath.category,
            // riseOnHover: true, // Optional
          });

          // Add tooltip (label) - permanent makes it always visible
          marker.bindTooltip(name, {
            permanent: true,
            direction: 'top', // Adjust as needed ('auto', 'right', 'left', 'bottom')
            className: 'labels', // Use your existing CSS class
            offset: L.point(0, -icon.options.iconSize[1] / 2) // Offset from icon anchor (center top often works)
            // Tweak offset: L.point(x, y) based on icon anchor and desired label position
          });


          // Add click listener
          marker.on('click', () => {
            this.handleAthleteClick(ath.id);
          });

          marker.addTo(athleteGroup); // Add to the group layer
          this.athletemarkers[id] = marker;

          // Re-enable Firebase listener setup if needed
          // this.setAthleteFirebaseListener(ath);
        }
      });
    },

    handleAthleteClick(athId) {
      console.log('Athlete clicked:', athId);
      // self.$ma.trackEvent(...) // Keep tracking if needed
      const routeName = this.mobile ? 'mapathlete' : 'athlete'; // Use correct mobile route name
      this.$router.push({ name: routeName, params: { id: athId } });
      // No Vuex action needed here based on original code, router handles selection state
    },


    // --- Single Athlete Path ---
    clearAthletePath() {
      if (this.athletePathLayer) {
        this.map.removeLayer(this.athletePathLayer);
        this.athletePathLayer = null;
      }
    },

    // --- Custom Control ---
    createFilterControl() {
      const FilterControl = L.Control.extend({
        onAdd: (map) => {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom'); // Use standard Leaflet CSS classes for styling
          container.style.color = 'black'
          container.style.backgroundColor = 'white';
          container.style.padding = '5px';
          container.style.width = 'auto'; // Adjust as needed
          container.style.cursor = 'pointer';
          container.innerHTML = this.showFavs ? 'Show All' : 'Show Favourites'; // Initial text

          container.onclick = (e) => {
            L.DomEvent.stopPropagation(e); // Prevent map click
            this.showFavs = !this.showFavs; // Toggle the state
            container.innerHTML = this.showFavs ? 'Show All' : 'Show Favourites'; // Update button text
            // Trigger marker update via watcher on showFavs or directly call update method
            this.updateAthleteMarkers(); // Directly trigger update for immediate response
          };
          return container;
        },
        onRemove: (map) => {
          // Cleanup if needed
        }
      });

      // Add instance of the control to the map
      new FilterControl({ position: 'topleft' }).addTo(this.map); // Adjust position
    },


    // --- Firebase (Keep if used) ---
    setAthleteFirebaseListener(ath) {
      // Original logic was commented out, adapt if needed for Leaflet
      // let self = this
      // let imei = ath.imei
      // let ref = db.ref('device_fixes/d' + imei + '/geometry') // Assuming `db` is your Firebase instance
      // let onValueChange = ref.on('value', function (snap) {
      //   if (snap.val()) {
      //     const geometry = snap.val();
      //     const latLng = [geometry.coordinates[1], geometry.coordinates[0]]; // Lng, Lat from GeoJSON standard
      //     const marker = self.athletemarkers[ath.id];
      //     if (marker) {
      //        // Check timestamp logic if needed (like original setAthletePosition)
      //        marker.setLatLng(latLng);
      //     }
      //   }
      // })
      // this.firebaseRefs.push({ ref: ref, onValueChange: onValueChange, athleteId: ath.id });
    },

  },
  mounted() {
    // Ensure the map container is ready
    this.$nextTick(() => {
      this.createMap();
      // Set initial selected state from route
      if (this.$route.name === 'athlete' || this.$route.name === 'mapathlete') {
        this.selected = this.$route.params.id;
      }
      this.updateAthleteMarkers(); // Ensure markers reflect initial selection
    });
  },
  beforeDestroy() {
    // Clean up the map instance to prevent memory leaks
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    // Clean up Firebase listeners
    // this.firebaseRefs.forEach(refData => refData.ref.off('value', refData.onValueChange));
    // this.firebaseRefs = [];
  }
};
</script>

<style>
/* Make sure map container has a height */
#mapcont {
  flex: 1 1;
  display: flex;
  position: relative;
  min-height: 400px; /* Example minimum height */
}

#map {
  flex: 1;
  height: 100%; /* Ensure map fills its container */
  width: 100%;
}

/* Style for Leaflet tooltips (replaces .labels) */
.leaflet-tooltip.labels { /* Target tooltips with your custom class */
  background-color: rgba(36, 47, 54, 0.95);
  border: 1px solid black;
  border-radius: 4px;
  color: #fff;
  padding: 1px 4px; /* Adjust padding */
  font-family: "Lucida Grande", "Arial", sans-serif;
  font-size: 10px;
  text-align: center;
  white-space: nowrap;
  /* Leaflet handles positioning, but you might need !important for overrides */
  box-shadow: none; /* Remove default Leaflet tooltip shadow if desired */
  /* Remove default Leaflet arrow */
  /* border: none; */
  /* background: transparent; */
}

/* Optional: Style for the custom Favourites control */
.leaflet-control-custom {
  /* Add specific styles if needed beyond leaflet-bar */
  text-align: center;
}

/* Remove default Leaflet tooltip arrow if using permanent labels */
.leaflet-tooltip-top.labels::before,
.leaflet-tooltip-bottom.labels::before,
.leaflet-tooltip-left.labels::before,
.leaflet-tooltip-right.labels::before {
  border: none !important; /* Hide the default arrow */
}


/* Keep cplabels if they are used elsewhere, otherwise remove */
/*
.cplabels {
  color: #000000;
  background-color: rgba(255, 255, 255, 0.46);
  border: 1px solid black;
  border-radius: 4px;
  padding: 1px;
  font-family: "Lucida Grande", "Arial", sans-serif;
  font-size: 10px;
  text-align: center;
  white-space: nowrap;
}
*/
</style>



// WEBPACK FOOTER //
// src/components/leaflet.vue